import { HStack, PageContainer, PageHeader, Spread } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { Filter } from '@/components/DataTable/FilterControls'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import {
  type CrtVersionField,
  useCrtVersionSelectionSync,
} from '@/hooks/crt/useCrtVersionSelectionSync'
import useBrandFilter from '@/hooks/filters/useBrandFilter'
import useMarkingFilter from '@/hooks/filters/useMarkingFilter'
import useMaterialGroupFilter from '@/hooks/filters/useMaterialGroupFilter'
import usePackContentSizeFilter from '@/hooks/filters/usePackContentSizeFilter'
import useProductCategoryFilter from '@/hooks/filters/useProductCategoryFilter'
import useProductTypeFilter from '@/hooks/filters/useProductTypeFilter'
import useShowValidStatusFilter from '@/hooks/filters/useShowValidStatusFilter'
import useVariantFilter from '@/hooks/filters/useVariantFilter'
import {
  pricingRatesExportQueryOptions,
  pricingRatesSearchSchema,
  usePricingRates,
} from '@/hooks/queries/crt/usePricingRates'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { useTablePagination } from '@/hooks/useTablePagination'
import { getClearedFilters } from '@/lib/utils/filters'
import { queryClient } from '@/main'
import { parseDateOnly } from '@/utils/DateOnlyHelpers'
import { PricingRateType } from '@repo/backend/crt'
import {
  INDETERMINATE,
  PRICING_RATE_TYPE_VALUES,
  isPricingRateTypePercentage,
} from '@repo/constants'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { addYears, eachMonthOfInterval, endOfYear, format } from 'date-fns'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type AssumptionKind,
  type AssumptionSelection,
  toAssumptionBadges,
  toSelectionSearch,
} from '../../-components/assumptions/assumption-selection'
import { ConfigureAssumptions } from '../../-components/assumptions/ConfigureAssumptions'
import { PricingRateDataTable } from './-components/PricingRateDataTable'

const ASSUMPTION_KINDS: AssumptionKind[] = ['pricing', 'tax']
const VERSION_FIELDS: CrtVersionField[] = ['pricingAssumptionIds', 'taxAssumptionIds']

export const Route = createFileRoute('/_auth/_modules/crt/pricing-excise/pricing-rate-table/')({
  component: RouteComponent,
  validateSearch: (search) => pricingRatesSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation('crt')
  const { formatMonthYear } = useLocalizedDate()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { crtPeriod } = useCrtVersionSelectionSync({
    search,
    navigate: (search) => navigate({ search }),
    fields: VERSION_FIELDS,
  })

  const [isConfigOpen, setIsConfigOpen] = useState(false)

  // Editing the selection keeps the loaded version, as the P&L does: the rates stay on the period's captured
  // prices with the edited assumptions layered on top.
  const handleApplyConfig = (selection: AssumptionSelection) => {
    navigate({ search: { ...search, ...toSelectionSearch(ASSUMPTION_KINDS, selection), page: 1 } })
  }

  const configuration = {
    pricingAssumptionIds: search.pricingAssumptionIds,
    taxAssumptionIds: search.taxAssumptionIds,
  }
  const configBadges = toAssumptionBadges(t, ASSUMPTION_KINDS, configuration)

  const [selectedView, setSelectedView] = useState<PricingRateType>(search.view ?? 'rrp')
  const crtPeriodDate = parseDateOnly(crtPeriod)
  const periodOptions = eachMonthOfInterval({
    start: crtPeriodDate,
    end: endOfYear(addYears(crtPeriodDate, 1)),
  }).map((date) => format(date, 'yyyy-MM'))
  const selectedPeriod =
    search.period && periodOptions.includes(search.period) ? search.period : periodOptions[0]!

  const { productCategoryFilter, productCategories, setProductCategories } =
    useProductCategoryFilter(search.productCategory)
  const { materialGroupFilter, materialGroups, setMaterialGroups } = useMaterialGroupFilter(
    search.materialGroupCode,
  )
  // Orderable brands and variants are the ones the period's price list copy prices.
  const { brandFilter, brands, setBrands } = useBrandFilter(search.brand, {
    isOrderable: true,
    crtPeriod,
  })
  const { variantFilter, variants, setVariants } = useVariantFilter(search.variant, {
    isOrderable: true,
    crtPeriod,
  })
  const { packContentSizeFilter, packContentSizes, setPackContentSizes } = usePackContentSizeFilter(
    search.packContentSize,
  )
  const { productTypeFilter, productTypes, setProductTypes } = useProductTypeFilter(
    search.productType,
  )
  const { markingFilter, marking, setMarking } = useMarkingFilter(search.marking, {
    title: t('pricingRateTable.filters.marking'),
  })
  const isPercentageView = isPricingRateTypePercentage(selectedView)
  const { showValidStatusFilter, showValidStatus, setShowValidStatus } = useShowValidStatusFilter(
    search.showValidStatus,
    isPercentageView,
  )
  const [keyword, setKeyword] = useState<string>(search.keyword ?? '')

  const selectedFilters = {
    productCategory: productCategories?.length ? productCategories : undefined,
    materialGroupCode: materialGroups?.length ? materialGroups : undefined,
    brand: brands?.length ? brands : undefined,
    variant: variants?.length ? variants : undefined,
    packContentSize: packContentSizes?.length ? packContentSizes : undefined,
    productType: productTypes?.length ? productTypes : undefined,
    marking: marking?.length ? marking : undefined,
    showValidStatus: showValidStatus !== INDETERMINATE ? showValidStatus : undefined,
  }

  const filters = [
    productCategoryFilter,
    materialGroupFilter,
    brandFilter,
    variantFilter,
    packContentSizeFilter,
    productTypeFilter,
    markingFilter,
    showValidStatusFilter,
  ]

  const pricingRatesQuery = usePricingRates({
    type: [selectedView],
    period: selectedPeriod,
    productCategory: search.productCategory,
    materialGroupCode: search.materialGroupCode,
    brand: search.brand,
    variant: search.variant,
    packContentSize: search.packContentSize,
    productType: search.productType,
    marking: search.marking,
    keyword: search.keyword,
    showValidStatus: search.showValidStatus,
    ...configuration,
    crtPeriod,
    page: search.page ?? 1,
    pageSize: pageSize,
    sort: search.sort,
  })

  const handleViewChange = (value: PricingRateType) => {
    setSelectedView(value)
    if (isPricingRateTypePercentage(value)) {
      setShowValidStatus(INDETERMINATE)
    }
    navigate({
      search: {
        ...search,
        view: value,
        showValidStatus: isPricingRateTypePercentage(value) ? undefined : search.showValidStatus,
        page: 1,
      },
    })
  }

  const handlePeriodChange = (value: string) => {
    navigate({ search: { ...search, period: value, page: 1 } })
  }

  const showResults = () => {
    navigate({
      search: {
        ...search,
        ...selectedFilters,
        keyword: keyword || undefined,
        page: 1,
      },
    })
  }

  const resetAllFilters = () => {
    setKeyword('')
    setProductCategories([])
    setMaterialGroups([])
    setBrands([])
    setVariants([])
    setPackContentSizes([])
    setProductTypes([])
    setMarking([])
    setShowValidStatus(INDETERMINATE)

    const clearedFilters = getClearedFilters(selectedFilters)

    navigate({
      search: {
        ...search,
        ...clearedFilters,
        keyword: undefined,
        page: 1,
      },
    })
  }

  const getCsvExport = () =>
    queryClient.ensureQueryData(
      pricingRatesExportQueryOptions({
        productCategory: search.productCategory,
        materialGroupCode: search.materialGroupCode,
        brand: search.brand,
        variant: search.variant,
        packContentSize: search.packContentSize,
        productType: search.productType,
        marking: search.marking,
        keyword: search.keyword,
        showValidStatus: search.showValidStatus,
        ...configuration,
        crtPeriod,
      }),
    )

  return (
    <PageContainer>
      <Spread>
        <PageHeader>{t('pricingRateTable.pageHeader')}</PageHeader>
        <HStack gap="sm">
          <ExportCsvButton
            getCsvExport={getCsvExport}
            rowCount={pricingRatesQuery.data?.rowCount ?? 0}
            label={t('export.viewData', { ns: 'common' })}
          />

          <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
            {/* Sits on the grey page header, so it needs its own white background. */}
            <SelectTrigger className="bg-background w-[200px]">
              <SelectValue placeholder={t('pricingRateTable.selectPeriod')} />
            </SelectTrigger>
            <SelectContent>
              {periodOptions.map((value) => (
                <SelectItem key={value} value={value}>
                  {formatMonthYear(parseDateOnly(`${value}-01`))}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </HStack>
      </Spread>

      <Card>
        <CardContent className="pt-6">
          <PricingRateDataTable
            viewType={selectedView || 'ptt'}
            period={selectedPeriod}
            filters={filters as Filter<unknown>[]}
            search={{
              className: 'w-[28rem]',
              value: keyword,
              setValue: setKeyword,
              placeholder: t('pricingRateTable.searchPlaceholder'),
            }}
            actions={
              <Select value={selectedView} onValueChange={handleViewChange}>
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder={t('pricingRateTable.selectView')} />
                </SelectTrigger>
                <SelectContent>
                  {PRICING_RATE_TYPE_VALUES.map((option) => (
                    <SelectItem key={option} value={option}>
                      {t(`pricingRateTable.views.${option}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            pricingRatesQuery={pricingRatesQuery}
            onShowResults={showResults}
            onResetAllFilters={resetAllFilters}
            toolbarActions={
              <ConfigureAssumptions
                isOpen={isConfigOpen}
                onOpenChange={setIsConfigOpen}
                crtPeriod={crtPeriod}
                kinds={ASSUMPTION_KINDS}
                selection={configuration}
                onApply={handleApplyConfig}
              />
            }
            configBadges={configBadges}
            onConfigBadgeClick={() => setIsConfigOpen(true)}
          />
        </CardContent>
      </Card>
    </PageContainer>
  )
}
